// This file is auto-generated by @hey-api/openapi-ts

import type { Options as ClientOptions, TDataShape, Client } from '@hey-api/client-fetch';
import type { GetHealthData, GetHealthResponses, GetMeData, GetMeResponses, GetMeErrors, ListCustomersData, ListCustomersResponses, ListCustomersErrors, ListMachinesData, ListMachinesResponses, ListMachinesErrors, ListMachineChecklistsData, ListMachineChecklistsResponses, ListMachineChecklistsErrors, ListMachineChecklistRunsData, ListMachineChecklistRunsResponses, ListMachineChecklistRunsErrors, ListMachineChecklistDefinitionsManifestData, ListMachineChecklistDefinitionsManifestResponses, ListMachineChecklistDefinitionsManifestErrors, HeadMachineChecklistDefinitionsManifestData, HeadMachineChecklistDefinitionsManifestResponses, HeadMachineChecklistDefinitionsManifestErrors, ListMachineChecklistDefinitionsExportData, ListMachineChecklistDefinitionsExportResponses, ListMachineChecklistDefinitionsExportErrors, HeadMachineChecklistDefinitionsExportData, HeadMachineChecklistDefinitionsExportResponses, HeadMachineChecklistDefinitionsExportErrors, ListChecklistVersionRunsData, ListChecklistVersionRunsResponses, ListChecklistVersionRunsErrors, ListChecklistRunsPagedData, ListChecklistRunsPagedResponses, ListChecklistRunsPagedErrors, GetChecklistRunDetailsData, GetChecklistRunDetailsResponses, GetChecklistRunDetailsErrors, GetChecklistRunsFilterOptionsData, GetChecklistRunsFilterOptionsResponses, GetChecklistRunsFilterOptionsErrors, MarkChecklistRunReadData, MarkChecklistRunReadResponses, MarkChecklistRunReadErrors, CreateChecklistRunSupportRequestData, CreateChecklistRunSupportRequestResponses, CreateChecklistRunSupportRequestErrors, ListChecklistRunItemsData, ListChecklistRunItemsResponses, ListChecklistRunItemsErrors, ListChecklistRunOlderChecksData, ListChecklistRunOlderChecksResponses, ListChecklistRunOlderChecksErrors, GetChecklistRunItemMediaContentData, GetChecklistRunItemMediaContentResponses, GetChecklistRunItemMediaContentErrors, GetChecklistRunStatisticsData, GetChecklistRunStatisticsResponses, GetChecklistRunStatisticsErrors, ImportMachineChecklistData, ImportMachineChecklistResponses, ImportMachineChecklistErrors } from './types.gen';
import { client as _heyApiClient } from './client.gen';

export type Options<TData extends TDataShape = TDataShape, ThrowOnError extends boolean = boolean> = ClientOptions<TData, ThrowOnError> & {
    /**
     * You can provide a client instance returned by `createClient()` instead of
     * individual options. This might be also useful if you want to implement a
     * custom client.
     */
    client?: Client;
    /**
     * You can pass arbitrary values through the `meta` object. This can be
     * used to access values that aren't defined as part of the SDK function.
     */
    meta?: Record<string, unknown>;
};

/**
 * Health check
 */
export const getHealth = <ThrowOnError extends boolean = false>(options?: Options<GetHealthData, ThrowOnError>) => {
    return (options?.client ?? _heyApiClient).get<GetHealthResponses, unknown, ThrowOnError>({
        url: '/health',
        ...options
    });
};

/**
 * Get current session user
 */
export const getMe = <ThrowOnError extends boolean = false>(options?: Options<GetMeData, ThrowOnError>) => {
    return (options?.client ?? _heyApiClient).get<GetMeResponses, GetMeErrors, ThrowOnError>({
        security: [
            {
                in: 'cookie',
                name: 'session',
                type: 'apiKey'
            }
        ],
        url: '/v1/me',
        ...options
    });
};

/**
 * List customers for Langguth customer context (shop customer rows)
 * Allowed for langguth_admin and langguth_kd only. Customer users receive 403.
 */
export const listCustomers = <ThrowOnError extends boolean = false>(options?: Options<ListCustomersData, ThrowOnError>) => {
    return (options?.client ?? _heyApiClient).get<ListCustomersResponses, ListCustomersErrors, ThrowOnError>({
        security: [
            {
                in: 'cookie',
                name: 'session',
                type: 'apiKey'
            }
        ],
        url: '/v1/customers',
        ...options
    });
};

/**
 * List machines (shop customer_machine)
 * Customer users use session customer. Langguth admin/kd may omit X-Customer-Id to list machines across all customers, or set X-Customer-Id to scope to one customer.
 */
export const listMachines = <ThrowOnError extends boolean = false>(options?: Options<ListMachinesData, ThrowOnError>) => {
    return (options?.client ?? _heyApiClient).get<ListMachinesResponses, ListMachinesErrors, ThrowOnError>({
        security: [
            {
                in: 'cookie',
                name: 'session',
                type: 'apiKey'
            }
        ],
        url: '/v1/machines',
        ...options
    });
};

/**
 * List checklists for a machine (current version metadata)
 */
export const listMachineChecklists = <ThrowOnError extends boolean = false>(options: Options<ListMachineChecklistsData, ThrowOnError>) => {
    return (options.client ?? _heyApiClient).get<ListMachineChecklistsResponses, ListMachineChecklistsErrors, ThrowOnError>({
        security: [
            {
                in: 'cookie',
                name: 'session',
                type: 'apiKey'
            }
        ],
        url: '/v1/machines/{machine_id}/checklists',
        ...options
    });
};

/**
 * List checklist runs across all versions for one checklist on this machine
 * Same response shape as checklist-version runs, but includes rows for any checklist_version_id linked to this checklist (e.g. runs created before a definition republish bumped current_version_id).
 */
export const listMachineChecklistRuns = <ThrowOnError extends boolean = false>(options: Options<ListMachineChecklistRunsData, ThrowOnError>) => {
    return (options.client ?? _heyApiClient).get<ListMachineChecklistRunsResponses, ListMachineChecklistRunsErrors, ThrowOnError>({
        security: [
            {
                in: 'cookie',
                name: 'session',
                type: 'apiKey'
            }
        ],
        url: '/v1/machines/{machine_id}/checklists/{checklist_id}/runs',
        ...options
    });
};

/**
 * Lightweight manifest of checklists on a machine (for metered sync)
 * Same auth as definitions export (export API key or browser session with access to the machine's customer). Returns checklistId and maxVersionUpdatedAt (UTC) per checklist. Send If-None-Match with the ETag from a previous response to receive 304 with no body when nothing changed. GET may return Content-Encoding gzip when Accept-Encoding includes gzip and the payload is large enough.
 *
 */
export const listMachineChecklistDefinitionsManifest = <ThrowOnError extends boolean = false>(options: Options<ListMachineChecklistDefinitionsManifestData, ThrowOnError>) => {
    return (options.client ?? _heyApiClient).get<ListMachineChecklistDefinitionsManifestResponses, ListMachineChecklistDefinitionsManifestErrors, ThrowOnError>({
        security: [
            {
                scheme: 'bearer',
                type: 'http'
            },
            {
                name: 'X-Api-Key',
                type: 'apiKey'
            },
            {
                in: 'cookie',
                name: 'session',
                type: 'apiKey'
            }
        ],
        url: '/v1/machines/{machine_id}/checklists/definitions/manifest',
        ...options
    });
};

/**
 * Manifest headers and ETag only
 */
export const headMachineChecklistDefinitionsManifest = <ThrowOnError extends boolean = false>(options: Options<HeadMachineChecklistDefinitionsManifestData, ThrowOnError>) => {
    return (options.client ?? _heyApiClient).head<HeadMachineChecklistDefinitionsManifestResponses, HeadMachineChecklistDefinitionsManifestErrors, ThrowOnError>({
        security: [
            {
                scheme: 'bearer',
                type: 'http'
            },
            {
                name: 'X-Api-Key',
                type: 'apiKey'
            },
            {
                in: 'cookie',
                name: 'session',
                type: 'apiKey'
            }
        ],
        url: '/v1/machines/{machine_id}/checklists/definitions/manifest',
        ...options
    });
};

/**
 * Export portable YAML for checklist versions on a machine
 * Authenticates with the export shared secret (Authorization Bearer or X-Api-Key) or with the same browser session cookie as other checklist routes (user must be allowed to access the machine's shop customer). Customer is derived from the shop machine row; the machine must exist. Returns one array entry per checklist_version row matching the filters, ordered by checklist then version number. Optional updated_after returns only versions with updated_at after the timestamp (row-level delta). Optional checklist_id restricts to one checklist (404 if not on this machine). Send If-None-Match with the ETag from a prior response for 304 with no body when the snapshot is unchanged. Use Accept-Encoding gzip for smaller payloads on metered links. isCurrentVersion is true when this version is the checklist's current_version_id. YAML matches the import document shape (no customer or machine inside the document).
 *
 */
export const listMachineChecklistDefinitionsExport = <ThrowOnError extends boolean = false>(options: Options<ListMachineChecklistDefinitionsExportData, ThrowOnError>) => {
    return (options.client ?? _heyApiClient).get<ListMachineChecklistDefinitionsExportResponses, ListMachineChecklistDefinitionsExportErrors, ThrowOnError>({
        security: [
            {
                scheme: 'bearer',
                type: 'http'
            },
            {
                name: 'X-Api-Key',
                type: 'apiKey'
            },
            {
                in: 'cookie',
                name: 'session',
                type: 'apiKey'
            }
        ],
        url: '/v1/machines/{machine_id}/checklists/definitions',
        ...options
    });
};

/**
 * Definitions ETag only (no body)
 */
export const headMachineChecklistDefinitionsExport = <ThrowOnError extends boolean = false>(options: Options<HeadMachineChecklistDefinitionsExportData, ThrowOnError>) => {
    return (options.client ?? _heyApiClient).head<HeadMachineChecklistDefinitionsExportResponses, HeadMachineChecklistDefinitionsExportErrors, ThrowOnError>({
        security: [
            {
                scheme: 'bearer',
                type: 'http'
            },
            {
                name: 'X-Api-Key',
                type: 'apiKey'
            },
            {
                in: 'cookie',
                name: 'session',
                type: 'apiKey'
            }
        ],
        url: '/v1/machines/{machine_id}/checklists/definitions',
        ...options
    });
};

/**
 * List checklist runs for a version
 */
export const listChecklistVersionRuns = <ThrowOnError extends boolean = false>(options: Options<ListChecklistVersionRunsData, ThrowOnError>) => {
    return (options.client ?? _heyApiClient).get<ListChecklistVersionRunsResponses, ListChecklistVersionRunsErrors, ThrowOnError>({
        security: [
            {
                in: 'cookie',
                name: 'session',
                type: 'apiKey'
            }
        ],
        url: '/v1/checklist-versions/{version_id}/runs',
        ...options
    });
};

/**
 * List checklist runs (paged, sortable, filterable)
 * Customer users see only their session customer. Langguth admin/kd users may omit X-Customer-Id to list runs across all customers, or set X-Customer-Id to scope to one customer.
 */
export const listChecklistRunsPaged = <ThrowOnError extends boolean = false>(options?: Options<ListChecklistRunsPagedData, ThrowOnError>) => {
    return (options?.client ?? _heyApiClient).get<ListChecklistRunsPagedResponses, ListChecklistRunsPagedErrors, ThrowOnError>({
        security: [
            {
                in: 'cookie',
                name: 'session',
                type: 'apiKey'
            }
        ],
        url: '/v1/checklist-runs',
        ...options
    });
};

/**
 * Full checklist run details (list row plus items and media metadata)
 * Customer users are scoped to their session customer. Langguth admin/kd may omit X-Customer-Id; tenant is derived from the run id.
 */
export const getChecklistRunDetails = <ThrowOnError extends boolean = false>(options: Options<GetChecklistRunDetailsData, ThrowOnError>) => {
    return (options.client ?? _heyApiClient).get<GetChecklistRunDetailsResponses, GetChecklistRunDetailsErrors, ThrowOnError>({
        security: [
            {
                in: 'cookie',
                name: 'session',
                type: 'apiKey'
            }
        ],
        url: '/v1/checklist-runs/{run_id}',
        ...options
    });
};

/**
 * Distinct filter values for checklist runs (employees, machines, checklists with runs)
 * Scoped to X-Customer-Id when set for Langguth users; when omitted, options cover all customers.
 */
export const getChecklistRunsFilterOptions = <ThrowOnError extends boolean = false>(options?: Options<GetChecklistRunsFilterOptionsData, ThrowOnError>) => {
    return (options?.client ?? _heyApiClient).get<GetChecklistRunsFilterOptionsResponses, GetChecklistRunsFilterOptionsErrors, ThrowOnError>({
        security: [
            {
                in: 'cookie',
                name: 'session',
                type: 'apiKey'
            }
        ],
        url: '/v1/checklist-runs/filter-options',
        ...options
    });
};

/**
 * Mark a checklist run as read for the current user
 */
export const markChecklistRunRead = <ThrowOnError extends boolean = false>(options: Options<MarkChecklistRunReadData, ThrowOnError>) => {
    return (options.client ?? _heyApiClient).post<MarkChecklistRunReadResponses, MarkChecklistRunReadErrors, ThrowOnError>({
        security: [
            {
                in: 'cookie',
                name: 'session',
                type: 'apiKey'
            }
        ],
        url: '/v1/checklist-runs/{run_id}/read',
        ...options
    });
};

/**
 * Queue a HubSpot support ticket for this checklist run
 * Persists the request for asynchronous processing. Requires hubspot.api_key and checklisten.public_base_url on the server.
 */
export const createChecklistRunSupportRequest = <ThrowOnError extends boolean = false>(options: Options<CreateChecklistRunSupportRequestData, ThrowOnError>) => {
    return (options.client ?? _heyApiClient).post<CreateChecklistRunSupportRequestResponses, CreateChecklistRunSupportRequestErrors, ThrowOnError>({
        security: [
            {
                in: 'cookie',
                name: 'session',
                type: 'apiKey'
            }
        ],
        url: '/v1/checklist-runs/{run_id}/support',
        ...options,
        headers: {
            'Content-Type': 'application/json',
            ...options.headers
        }
    });
};

/**
 * Per-item outcomes and media metadata for a checklist run
 */
export const listChecklistRunItems = <ThrowOnError extends boolean = false>(options: Options<ListChecklistRunItemsData, ThrowOnError>) => {
    return (options.client ?? _heyApiClient).get<ListChecklistRunItemsResponses, ListChecklistRunItemsErrors, ThrowOnError>({
        security: [
            {
                in: 'cookie',
                name: 'session',
                type: 'apiKey'
            }
        ],
        url: '/v1/checklist-runs/{run_id}/items',
        ...options
    });
};

/**
 * Matching checklist item instances from older runs (same machine and checklist) for history search
 * Tenant is derived from the run id. Returns item-level rows from prior runs ordered by completedAt descending. Requires a non-empty search string (title substring match); otherwise returns an empty list.
 */
export const listChecklistRunOlderChecks = <ThrowOnError extends boolean = false>(options: Options<ListChecklistRunOlderChecksData, ThrowOnError>) => {
    return (options.client ?? _heyApiClient).get<ListChecklistRunOlderChecksResponses, ListChecklistRunOlderChecksErrors, ThrowOnError>({
        security: [
            {
                in: 'cookie',
                name: 'session',
                type: 'apiKey'
            }
        ],
        url: '/v1/checklist-runs/{run_id}/older-checks',
        ...options
    });
};

/**
 * Download binary media attached to a checklist item instance
 * Streams the object from configured object storage. Returns 503 when S3 is not configured.
 */
export const getChecklistRunItemMediaContent = <ThrowOnError extends boolean = false>(options: Options<GetChecklistRunItemMediaContentData, ThrowOnError>) => {
    return (options.client ?? _heyApiClient).get<GetChecklistRunItemMediaContentResponses, GetChecklistRunItemMediaContentErrors, ThrowOnError>({
        security: [
            {
                in: 'cookie',
                name: 'session',
                type: 'apiKey'
            }
        ],
        url: '/v1/checklist-runs/{run_id}/items/{item_instance_id}/media/{media_id}',
        ...options
    });
};

/**
 * Completion KPIs for the dashboard (current customer)
 */
export const getChecklistRunStatistics = <ThrowOnError extends boolean = false>(options?: Options<GetChecklistRunStatisticsData, ThrowOnError>) => {
    return (options?.client ?? _heyApiClient).get<GetChecklistRunStatisticsResponses, GetChecklistRunStatisticsErrors, ThrowOnError>({
        security: [
            {
                in: 'cookie',
                name: 'session',
                type: 'apiKey'
            }
        ],
        url: '/v1/checklist-run-statistics',
        ...options
    });
};

/**
 * Import a checklist from YAML or JSON for a machine
 */
export const importMachineChecklist = <ThrowOnError extends boolean = false>(options: Options<ImportMachineChecklistData, ThrowOnError>) => {
    return (options.client ?? _heyApiClient).post<ImportMachineChecklistResponses, ImportMachineChecklistErrors, ThrowOnError>({
        security: [
            {
                in: 'cookie',
                name: 'session',
                type: 'apiKey'
            }
        ],
        url: '/v1/machines/{machine_id}/checklists/import',
        ...options,
        headers: {
            'Content-Type': 'application/json',
            ...options.headers
        }
    });
};